/**
 * KanbanColumn - Single column component for the kanban board.
 * @implements FS-005.9.1.1 - Column Structure (header with name and count, vertical scroll)
 * @implements FS-005.9.2.1 - Card Display (flat list within column, ordered by creation time)
 * @implements BS-005.9.3 - Cancelled Ticket Visibility
 *
 * Receives a list of tickets and renders them within a column container.
 * Header shows column display name and ticket count badge.
 * Content area scrolls vertically when ticket count exceeds visible area.
 * Cancelled tickets are filtered out before display.
 */

import { Box, Paper, Typography, Chip } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { KanbanColumn as KanbanColumnEnum } from '@/utils/kanban';
import { KanbanTicketPayload } from '@/stores/KanbanStore';
import { KanbanCard } from './KanbanCard';

/**
 * Human-readable display names for kanban columns.
 * @implements FS-005.9.1.1 - Column headers display column name
 */
const COLUMN_DISPLAY_NAMES: Record<KanbanColumnEnum, string> = {
  [KanbanColumnEnum.BACKLOG]: 'Backlog',
  [KanbanColumnEnum.CONSOLIDATION]: 'Consolidation',
  [KanbanColumnEnum.TODO]: 'Todo',
  [KanbanColumnEnum.IN_PROGRESS]: 'In Progress',
  [KanbanColumnEnum.REVIEW]: 'Review',
  [KanbanColumnEnum.QA]: 'QA',
  [KanbanColumnEnum.DONE]: 'Done',
};

/**
 * Background colors for column headers (subtle tints matching column semantics).
 * @implements FS-005.9.1.1 - Column Structure (consistent styling)
 */
const COLUMN_HEADER_COLORS: Record<KanbanColumnEnum, string> = {
  [KanbanColumnEnum.BACKLOG]: 'grey.200',
  [KanbanColumnEnum.CONSOLIDATION]: 'warning.50',
  [KanbanColumnEnum.TODO]: 'info.50',
  [KanbanColumnEnum.IN_PROGRESS]: 'info.100',
  [KanbanColumnEnum.REVIEW]: 'secondary.50',
  [KanbanColumnEnum.QA]: 'secondary.100',
  [KanbanColumnEnum.DONE]: 'success.50',
};

/**
 * Props for KanbanColumn component.
 */
export interface KanbanColumnProps {
  /** The kanban column this component represents */
  column: KanbanColumnEnum;
  /** Array of tickets in this column */
  tickets: KanbanTicketPayload[];
  /** Callback when a card is clicked */
  onCardClick?: (ticketId: string) => void;
}

/**
 * KanbanColumn - Renders a single kanban column with header and scrollable card list.
 *
 * @implements FS-005.9.1.1 - Column Structure (header with name and count, vertical scroll)
 * @implements FS-005.9.2.1 - Card Display (flat list within column)
 * @implements FS-005.9.4.3 - Card Movement Animation (AnimatePresence for exit animations)
 * @implements BS-005.9.3 - Cancelled Ticket Visibility (filtered out)
 *
 * Features:
 * - Header with column display name and ticket count badge
 * - Sticky header during vertical scroll
 * - Scrollable content area for tickets
 * - Cancelled tickets filtered out before display
 * - KanbanCard components with type badges and animations
 * - AnimatePresence wrapper for smooth card exit animations
 */
export const KanbanColumn = observer(
  ({ column, tickets, onCardClick }: KanbanColumnProps) => {
    // Filter out cancelled tickets
    // @implements BS-005.9.3 - Cancelled tickets are filtered out and not displayed
    const visibleTickets = tickets.filter(
      (ticket) => ticket.lifecycleState !== 'canceled',
    );

    const displayName = COLUMN_DISPLAY_NAMES[column];
    const ticketCount = visibleTickets.length;

    return (
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'grey.100',
          borderRadius: 2,
          minHeight: 0, // Allow flex child to shrink
          overflow: 'hidden',
        }}
        data-testid={`kanban-column-${column}`}
      >
        {/* Column header - sticky during scroll */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: 'grey.200',
            bgcolor: COLUMN_HEADER_COLORS[column] || 'grey.100',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
            }}
          >
            {displayName}
          </Typography>
          {/* Ticket count badge */}
          <Chip
            label={ticketCount}
            size="small"
            sx={{
              height: 20,
              minWidth: 24,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: 'grey.300',
              color: 'text.secondary',
              '& .MuiChip-label': {
                px: 0.75,
              },
            }}
          />
        </Box>

        {/* Column content - vertical scroll */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto', // Independent vertical scroll per column
            overflowX: 'hidden',
            p: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {/* @implements FS-005.9.4.3 - AnimatePresence for exit animations */}
          <AnimatePresence mode="popLayout">
            {visibleTickets.map((ticket) => (
              <KanbanCard
                key={ticket.id}
                ticket={ticket}
                onClick={onCardClick ? () => onCardClick(ticket.id) : undefined}
              />
            ))}
          </AnimatePresence>
        </Box>
      </Paper>
    );
  },
);

// Display name for React DevTools
KanbanColumn.displayName = 'KanbanColumn';
